// client/src/components/common/PickerSheet.tsx
//
// The bottom sheet the shared date/time pickers open on a phone or a touch screen (ST-00204):
// full width below 640 px (a centred 32rem sheet on a wider touch screen), at most ~85% of the
// viewport tall, a scrolling body and a footer that always shows large Cancel and Apply
// buttons. A labelled modal dialog: the page behind is scroll-locked, Esc / the backdrop / the
// close button / dragging the handle down all cancel, Tab stays inside, and focus returns to
// the trigger. Motion is dropped under prefers-reduced-motion (PickerSheet.css).
//
// Esc is taken on `window` in the capture phase, before anything on `document` sees it — the
// LexiStar Modal closes itself from a document capture listener, so a picker inside a dialog
// would otherwise close the dialog instead of the picker.
//
// On a desktop the same props open PickerPopover instead (ST-00294); PickerSurface chooses.

import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { useEscapeKey, useLatest } from './pickerHooks';
import './PickerSheet.css';

const FOCUSABLE = [
  'button:not([disabled]):not([tabindex="-1"])',
  'a[href]:not([tabindex="-1"])',
  'input:not([disabled]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export const focusableIn = (node: HTMLElement | null): HTMLElement[] =>
  node
    ? Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.getAttribute('aria-hidden') !== 'true' && !el.closest('[inert]')
      )
    : [];

/** Tab / Shift+Tab inside a picker dialog wrap round instead of leaving it (sheet and popup). */
export function keepTabInside(e: ReactKeyboardEvent<HTMLElement>, dialog: HTMLElement | null) {
  if (e.key !== 'Tab') return;
  const items = focusableIn(dialog);
  if (!items.length) {
    e.preventDefault();
    return;
  }
  const first = items[0]!;
  const last = items[items.length - 1]!;
  const active = document.activeElement;
  if (e.shiftKey && (active === first || active === dialog)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

/** Drag distance (px) past which letting go closes the sheet; a quick flick closes sooner. */
const DISMISS_DISTANCE = 96;
const FLICK_DISTANCE = 32;
const FLICK_SPEED = 0.6; // px per ms

export interface PickerSheetProps {
  open: boolean;
  title: string;
  /** Esc, backdrop, close button, Cancel and a drag down. Nothing is committed. */
  onCancel: () => void;
  onApply: () => void;
  applyLabel?: string;
  applyDisabled?: boolean;
  /** The trigger: focus returns to it on close; its scrolling ancestors are locked while open. */
  returnFocusRef?: RefObject<HTMLElement | null>;
  /** What gets focus on open (the selected day, the hour); defaults to the sheet itself. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Small action beside the title (e.g. Clear). */
  headerAction?: ReactNode;
  children: ReactNode;
}

export default function PickerSheet({
  open,
  title,
  onCancel,
  onApply,
  applyLabel = 'Apply',
  applyDisabled = false,
  returnFocusRef,
  initialFocusRef,
  headerAction,
  children,
}: PickerSheetProps) {
  const titleId = useId();
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const cancel = useLatest(onCancel);
  const [dragY, setDragY] = useState(0);
  const drag = useRef<{ y: number; t: number } | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const sheet = sheetRef.current;
    const opener =
      returnFocusRef?.current ??
      (document.activeElement instanceof HTMLElement ? document.activeElement : null);

    // Lock the page behind: the document and every scrolling ancestor of the trigger (the
    // app scrolls inside .page-pane, not the body).
    const locked: Array<[HTMLElement, string]> = [];
    const lock = (el: HTMLElement) => {
      locked.push([el, el.style.overflow]);
      el.style.overflow = 'hidden';
    };
    lock(document.documentElement);
    lock(document.body);
    for (let el = opener?.parentElement ?? null; el && el !== document.body; el = el.parentElement) {
      const { overflowY } = window.getComputedStyle(el);
      if (/(auto|scroll)/.test(overflowY) && el.scrollHeight > el.clientHeight) lock(el);
    }

    // Focus that lands outside (a screen reader's virtual cursor, a stray script) comes back.
    const onFocusIn = (e: FocusEvent) => {
      if (sheet && e.target instanceof Node && !sheet.contains(e.target)) {
        (focusableIn(sheet)[0] ?? sheet).focus({ preventScroll: true });
      }
    };
    document.addEventListener('focusin', onFocusIn);
    (initialFocusRef?.current ?? sheet)?.focus({ preventScroll: true });

    return () => {
      document.removeEventListener('focusin', onFocusIn);
      locked.reverse().forEach(([el, value]) => {
        el.style.overflow = value;
      });
      opener?.focus?.({ preventScroll: true });
    };
    // Runs once per opening; the refs are read at that moment on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) setDragY(0);
  }, [open]);

  useEscapeKey(open, () => cancel.current());

  if (!open || typeof document === 'undefined') return null;

  const trapTab = (e: ReactKeyboardEvent<HTMLDivElement>) => keepTabInside(e, sheetRef.current);

  // Drag the handle / header down to dismiss. Buttons in the header keep their own taps.
  const onDragStart = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if ((e.target as HTMLElement).closest('button')) return;
    drag.current = { y: e.clientY, t: e.timeStamp };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onDragMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    setDragY(Math.max(0, e.clientY - drag.current.y));
  };
  const onDragEnd = (e: ReactPointerEvent<HTMLDivElement>) => {
    const start = drag.current;
    drag.current = null;
    if (!start) return;
    const distance = Math.max(0, e.clientY - start.y);
    const speed = distance / Math.max(1, e.timeStamp - start.t);
    if (distance > DISMISS_DISTANCE || (distance > FLICK_DISTANCE && speed > FLICK_SPEED)) {
      cancel.current();
    } else {
      setDragY(0);
    }
  };
  const onDragCancel = () => {
    drag.current = null;
    setDragY(0);
  };

  return createPortal(
    <div className="picker-sheet-root">
      <div className="picker-sheet-backdrop" data-testid="picker-sheet-backdrop" onClick={onCancel} aria-hidden="true" />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`picker-sheet${dragY ? ' picker-sheet--dragging' : ''}`}
        style={dragY ? { transform: `translateY(${dragY}px)` } : undefined}
        onKeyDown={trapTab}
      >
        <div
          className="picker-sheet__grab"
          data-testid="picker-sheet-grab"
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragCancel}
        >
          <span className="picker-sheet__handle" aria-hidden="true" />
          <div className="picker-sheet__header">
            <h2 id={titleId} className="picker-sheet__title">
              {title}
            </h2>
            {headerAction}
            <button
              type="button"
              className="picker-sheet__close"
              onClick={onCancel}
              aria-label="Close without changing"
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
        <div className="picker-sheet__body">{children}</div>
        <div className="picker-sheet__footer">
          <button type="button" className="picker-sheet__cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="picker-sheet__apply"
            onClick={onApply}
            disabled={applyDisabled}
          >
            {applyLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
